import { Link } from "@/lib/router-compat";
import { useSEO } from "@/hooks/useSEO";
import { ArrowLeft, Lightbulb, CheckCircle, Sparkles, Brain } from "lucide-react";

import { ArticleTOC } from "@/components/resources/ArticleTOC";
import { ArticleJsonLd } from "@/components/resources/ArticleJsonLd";


const sections = [
  { id: "recap", title: "The Feynman Technique Recap" },
  { id: "ai-integration", title: "How AI Supercharges the Method" },
  { id: "workflow", title: "The 5-Step AI + Feynman Workflow" },
  { id: "examples", title: "Practical Examples" },
  { id: "pitfalls", title: "Pitfalls to Avoid" },
  { id: "key-takeaways", title: "Key Takeaways" },
];

export default function FeynmanAIGuide() {
  useSEO({
    title: "Feynman Technique + AI: How to Apply It to Your Courses | System Atlas",
    description: "Combine the Feynman technique with AI tools to learn faster. A practical guide to applying teach-to-learn with AI assistance on your own course materials.",
    type: "article",
    alternates: [
      { hreflang: "en", href: "https://systematlas.fr/learn/feynman-ai" },
      { hreflang: "fr", href: "https://systematlas.fr/fr/learn/feynman-ai" },
      { hreflang: "x-default", href: "https://systematlas.fr/learn/feynman-ai" },
    ],
  });

  return (
    <>
      <ArticleJsonLd title="Feynman Technique + AI: How to Apply It to Your Courses" description="Combine the Feynman technique with AI tools to learn faster." publishedDate="2026-02-23" modifiedDate="2026-02-23" inLanguage="en" />
      <div className="container mx-auto px-6 py-12 max-w-3xl">
        <Link to="/learn" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground text-sm mb-8"><ArrowLeft className="w-4 h-4" />Back to guides</Link>
        <article>
          <h1 className="text-3xl md:text-4xl font-serif font-bold mb-6 tracking-tight">Feynman Technique + AI: How to Apply It to Your Courses</h1>
          <p className="text-lg text-muted-foreground mb-8 leading-relaxed">The Feynman Technique. learning by teaching. is one of the most powerful study methods. AI tools can now act as your teaching audience, giving you instant feedback on your understanding. Here's how to combine both.</p>
          <ArticleTOC sections={sections} />

          <section id="recap" className="mb-10">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2"><Lightbulb className="w-5 h-5 text-primary" />The Feynman Technique Recap</h2>
            <p className="text-muted-foreground mb-4 leading-relaxed">The Feynman Technique has 4 steps: (1) Choose a concept, (2) Explain it as if teaching a child, (3) Identify gaps when you struggle, (4) Simplify and use analogies.</p>
            <p className="text-muted-foreground leading-relaxed">The problem? Most students skip step 3 because they don't have someone to test their explanation against. <strong className="text-foreground">AI solves this.</strong></p>
          </section>

          <section id="ai-integration" className="mb-10">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2"><Sparkles className="w-5 h-5 text-primary" />How AI Supercharges the Method</h2>
            <ul className="space-y-3">
              {[
                { b: "Instant feedback loop:", t: "Explain a concept to the AI, then ask it to check your explanation against the source material. It'll catch what you missed." },
                { b: "Socratic questioning:", t: "Ask the AI to challenge your explanation with 'why' and 'what if' questions. exactly what a real teacher would do." },
                { b: "Simplification partner:", t: "If your explanation is too complex, ask the AI to simplify it further. Compare its version with yours." },
                { b: "Gap identification:", t: "After explaining, ask: 'What important aspects of this concept did I miss?' The AI cross-references your explanation with the full content." },
              ].map(i => (
                <li key={i.b} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                  <span className="text-muted-foreground text-sm"><strong className="text-foreground">{i.b}</strong> {i.t}</span>
                </li>
              ))}
            </ul>
          </section>

          <section id="workflow" className="mb-10">
            <h2 className="text-xl font-semibold mb-4">The 5-Step AI + Feynman Workflow</h2>
            <div className="space-y-4">
              {[
                { n: "1", t: "Upload your course material", d: "Feed your PDF, slides, or notes into an AI study tool. This ensures all feedback is grounded in YOUR syllabus." },
                { n: "2", t: "Generate a revision sheet", d: "Let the AI create a structured sheet. Read it, but don't just absorb. prepare to explain it back." },
                { n: "3", t: "Explain the concept in chat", d: "Open the AI chat and explain the concept in your own words. Write as if teaching a classmate who missed the lecture." },
                { n: "4", t: "Ask for gaps and challenges", d: "Prompt: 'What did I miss? What's wrong in my explanation? Ask me a hard question about this.' The AI checks against the source." },
                { n: "5", t: "Iterate until clean", d: "Keep explaining and refining until you can give a complete, accurate explanation without looking at the sheet." },
              ].map(i => (
                <div key={i.n} className="p-4 border border-border/60 rounded-lg">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-medium text-sm">{i.n}</span>
                    <h3 className="font-medium">{i.t}</h3>
                  </div>
                  <p className="text-muted-foreground text-sm leading-relaxed">{i.d}</p>
                </div>
              ))}
            </div>
          </section>

          <section id="examples" className="mb-10">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2"><Brain className="w-5 h-5 text-primary" />Practical Examples</h2>
            <div className="space-y-4">
              <div className="p-5 bg-muted/30 border border-border/60 rounded-lg">
                <h3 className="font-medium mb-3 text-sm">Economics: Supply and Demand</h3>
                <p className="text-muted-foreground text-sm mb-2"><strong className="text-foreground">Your explanation:</strong> "When the price goes up, fewer people buy stuff, and more companies want to sell."</p>
                <p className="text-muted-foreground text-sm"><strong className="text-foreground">AI feedback:</strong> "Good intuition! But you're missing the concept of equilibrium. what happens when supply equals demand? Also, can you explain what shifts the curves vs. moves along them?"</p>
              </div>
              <div className="p-5 bg-muted/30 border border-border/60 rounded-lg">
                <h3 className="font-medium mb-3 text-sm">Biology: DNA Replication</h3>
                <p className="text-muted-foreground text-sm mb-2"><strong className="text-foreground">Your explanation:</strong> "DNA copies itself by splitting in half and each half gets rebuilt."</p>
                <p className="text-muted-foreground text-sm"><strong className="text-foreground">AI feedback:</strong> "The core idea is right (semi-conservative replication), but what enzyme unwinds the DNA? What's the difference between the leading and lagging strand? These details are likely exam material."</p>
              </div>
            </div>
          </section>

          <section id="pitfalls" className="mb-10">
            <h2 className="text-xl font-semibold mb-4">Pitfalls to Avoid</h2>
            <div className="space-y-3">
              {[
                { t: "Reading the AI's explanation instead of writing your own first", d: "The learning happens when YOU explain. Reading the AI's version is passive." },
                { t: "Accepting the AI's feedback without verifying", d: "AI can make mistakes. Always cross-check with your course materials." },
                { t: "Skipping the iteration step", d: "One round isn't enough. The real understanding comes from 2-3 cycles of explain → feedback → refine." },
              ].map(i => (
                <div key={i.t} className="p-4 border-l-4 border-destructive/40 bg-destructive/5 rounded-r-lg">
                  <p className="font-medium text-sm mb-1">{i.t}</p>
                  <p className="text-xs text-muted-foreground">{i.d}</p>
                </div>
              ))}
            </div>
          </section>

          <section id="key-takeaways" className="p-6 bg-primary/5 border border-primary/15 rounded-xl">
            <h2 className="text-lg font-medium mb-3">Key Takeaways</h2>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li>• AI acts as your teaching audience. available 24/7, patient, and course-specific</li>
              <li>• Always explain FIRST, then ask AI to check. not the other way around</li>
              <li>• Use "What did I miss?" and "Ask me a hard question" prompts</li>
              <li>• Iterate 2-3 times until your explanation is clean and complete</li>
              <li>• This method combines active recall, elaboration, and self-testing. the three most effective study strategies</li>
            </ul>
          </section>
        </article>

        <nav className="mt-12 pt-8 border-t border-border/40">
          <h3 className="font-medium mb-4">Related Guides</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <Link to="/learn/ai-quiz-generator" className="p-4 border border-border/60 rounded-lg hover:border-primary/40 transition-colors">
              <h4 className="font-medium mb-1 text-sm">Generate Quizzes with AI</h4>
              <p className="text-xs text-muted-foreground">Test your understanding with AI-generated MCQs</p>
            </Link>
            <Link to="/learn/feynman-technique" className="p-4 border border-border/60 rounded-lg hover:border-primary/40 transition-colors">
              <h4 className="font-medium mb-1 text-sm">The Feynman Technique</h4>
              <p className="text-xs text-muted-foreground">The original method explained</p>
            </Link>
            <Link to="/learn/active-recall" className="p-4 border border-border/60 rounded-lg hover:border-primary/40 transition-colors">
              <h4 className="font-medium mb-1 text-sm">Active Recall</h4>
              <p className="text-xs text-muted-foreground">The science of effective learning</p>
            </Link>
          </div>
        </nav>
      </div>
    </>
  );
}
